/**
 * Auth API functions
 * Typed wrappers for authentication endpoints
 */

import { api, ApiError } from './client';

// Types matching backend responses
export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  job_title?: string;
  role: 'global_admin' | 'global_reader' | 'channel_manager' | 'org_admin' | 'org_user';
  organization_id?: string;
  organization_name?: string;
  organization_tier?: string;
  organization_status?: string;
  organization_partner_type?: 'individual' | 'company';
  organization_partner_model?: 'referral' | 'resale' | 'both';
  mfa_enabled?: boolean;
  must_change_password?: boolean;
  created_at?: number;
}

export interface LoginRequest {
  email: string;
  password: string;
  turnstile_token?: string;
}

export interface LoginResponse {
  data: User | { requires_mfa: true; mfa_token: string };
}

export interface MfaLoginRequest {
  mfa_token: string;
  code: string;
}

export interface MfaLoginResponse {
  data: User;
}

export interface SignupRequest {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  partner_type: 'individual' | 'company';
  legal_name?: string;
  website?: string;
  entity_type?: string;
  state_of_incorporation?: string;
  // Company address (for companies)
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  // Signer/individual address
  signer_address_line1: string;
  signer_address_line2?: string;
  signer_city: string;
  signer_state: string;
  signer_postal_code: string;
  signer_country: string;
  // Partner model selection (only for company signups)
  // 'both' = resale + referral, 'referral_only' = referral only
  partner_model?: 'both' | 'referral_only';
}

export interface SignupResponse {
  data: {
    message: string;
    organization_id: string;
  };
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface ChangePasswordResponse {
  data: {
    message: string;
  };
}

export interface MeResponse {
  data: User;
}

/**
 * Login with email and password
 * Returns User if successful, or MFA challenge if MFA is enabled
 */
export async function login(credentials: LoginRequest): Promise<User | { requires_mfa: true; mfa_token: string }> {
  const response = await api.post<LoginResponse>('/auth/login', credentials);
  return response.data;
}

/**
 * Complete login with MFA verification code
 */
export async function loginWithMfa(request: MfaLoginRequest): Promise<User> {
  const response = await api.post<MfaLoginResponse>('/auth/login/mfa', request);
  return response.data;
}

/**
 * Sign up as a new partner
 */
export async function signup(data: SignupRequest): Promise<SignupResponse['data']> {
  const response = await api.post<SignupResponse>('/auth/signup', data);
  return response.data;
}

/**
 * Logout current session
 */
export async function logout(): Promise<void> {
  await api.post('/auth/logout', {});
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser(): Promise<User> {
  const response = await api.get<MeResponse>('/auth/me');
  return response.data;
}

/**
 * Refresh session (extend expiry)
 */
export async function refreshSession(): Promise<{ expiresAt: number }> {
  const response = await api.post<{ data: { expiresAt: number } }>('/auth/refresh', {});
  return response.data;
}

/**
 * Change password
 */
export async function changePassword(data: ChangePasswordRequest): Promise<string> {
  const response = await api.post<ChangePasswordResponse>('/auth/change-password', data);
  return response.data.message;
}

/**
 * Check if user is authenticated by calling /auth/me
 * Returns user if authenticated, null if not
 */
export async function checkAuth(): Promise<User | null> {
  try {
    return await getCurrentUser();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      return null;
    }
    throw error;
  }
}

// Re-export ApiError for convenience
export { ApiError };
